module App where

import Prelude

import Affjax (printError)
import Affjax as AX
import Affjax.RequestBody as AXB
import Affjax.RequestHeader (RequestHeader(..))
import Affjax.ResponseFormat (json)
import Affjax.Web (get, post, request)
import Clerk (Clerk)
import Clerk as Clerk
import Control.Monad.Error.Class (class MonadError, class MonadThrow)
import Control.Monad.Reader (class MonadAsk, ReaderT, asks, runReaderT)
import Data.Argonaut (Json, decodeJson, encodeJson)
import Data.Either (Either(..))
import Data.HTTP.Method (Method(..))
import Data.Maybe (Maybe(..))
import Data.Maybe as M
import Data.Traversable (traverse)
import Effect.Aff (Aff, Error, error, message)
import Effect.Aff.Class (class MonadAff, liftAff)
import Effect.Class (class MonadEffect, liftEffect)
import Effect.Exception (throwException)
import Foreign (unsafeToForeign)
import Hereabout.Data.Route (CommunityFeedTab(..), HereaboutPage(..), Route(..), routeCodec)
import Hereabout.Effects (class Favourite, class LocationFeed, class Navigate, class RenderExternalUserComponents, class ResolveRoute, class SharePost, class ShareURL, class UserSession)
import Hereabout.Feed.Json (HAPIJson(..))
import Hereabout.Navigator.Share as NS
import Hereabout.Social.Json (HSAPIJson(..))
import Hereabout.Types (EventWithComments(..), User(..))
import Hereabout.UI.Components.EventPreviewComponent (Action(..))
import Markdown.Markdown (sanitisedHTMLFromCommentMarkdown)
import Prim.Boolean (True)
import Routing.Duplex (print)
import Routing.Hash (setHash)
import Routing.PushState (PushStateInterface)
import Type.Equality (class TypeEquals, from)


type HereaboutEnv = 
  { baseUrl :: AX.URL
  , uiBaseUrl :: AX.URL
  , clerkInstance :: Clerk
  , pushStateInterface :: PushStateInterface
  }

newtype AppM a = AppM (ReaderT HereaboutEnv Aff a)

derive newtype instance funtorAppM :: Functor AppM
derive newtype instance applyAppM :: Apply AppM
derive newtype instance applicativeAppM :: Applicative AppM
derive newtype instance bindAppM :: Bind AppM
derive newtype instance monadAppM :: Monad AppM
derive newtype instance monadEffectAppM :: MonadEffect AppM
derive newtype instance monadAffAppM :: MonadAff AppM
derive newtype instance monadThrowAppM :: MonadThrow Error AppM
derive newtype instance monadErrorAppM :: MonadError Error AppM

instance monadAskAppM :: (TypeEquals e HereaboutEnv) => MonadAsk e AppM where
  ask = AppM $ asks from

instance navigateAppM :: Navigate AppM where
  navigate route = do
    --liftEffect $ setHash $ (print routeCodec) route
    psi <- asks (\e -> e.pushStateInterface)
    liftEffect $ psi.pushState (unsafeToForeign {}) $ (print routeCodec) route

    pure unit

instance resolveRouteAppM :: ResolveRoute AppM where
  resolveRoute (CommunityRoute id) = do
    baseUrl <- asks (\e -> e.baseUrl)
    eResp <- liftAff $ get json (baseUrl <> "/feed/community/" <> id)
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (HAPIJson stuff') -> pure $ CommunityFeedPage stuff' CommunityFeedEventTab
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed "  <> printError  err
  resolveRoute (CommunityRouteVenueTab id) = do
    baseUrl <- asks (\e -> e.baseUrl)
    eResp <- liftAff $ get json (baseUrl <> "/feed/community/" <> id)
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (HAPIJson stuff') -> pure $ CommunityFeedPage stuff' CommunityFeedVenueTab
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed "  <> printError  err
  resolveRoute (VenueRoute id) = do
    baseUrl <- asks (\e -> e.baseUrl)
    eResp <- liftAff $ get json (baseUrl <> "/feed/venue/" <> id)
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (HAPIJson stuff') -> pure $ VenuePage stuff'
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed "  <> printError  err
  resolveRoute (EventRoute id) = do
    baseUrl <- asks (\e -> e.baseUrl)
    inst <- asks (\e -> e.clerkInstance)
    maybeToken <- (liftEffect $ Clerk.getSession inst) >>= (\maybeSession -> M.maybe (pure Nothing)  (\session -> Just <$> (liftAff $ Clerk.getToken session)) maybeSession)
    let req' = AX.defaultRequest
          { content = Nothing
          , headers = M.maybe [] (\token -> [RequestHeader "Authorization" ("Bearer " <> token)]) maybeToken
          , method = Left GET
          , url = baseUrl <> "/event/" <> id
          , responseFormat = json
          }
    eResp <- liftAff $ request req'
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (HAPIJson (EventWithComments event')) -> do
            comments' <- liftEffect $ traverse sanitisedHTMLFromCommentMarkdown event'.comments
            let event'' = event' { comments = comments' }
            pure $ EventPage $ EventWithComments event''
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed "  <> printError  err
  resolveRoute CommunityListRoute = do
    baseUrl <- asks (\e -> e.baseUrl)
    eResp <- liftAff $ get json (baseUrl <> "/communities")
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (stuff') -> do
            let stuff'' = map (\(HAPIJson j) -> j) stuff'
            pure $ CommunityListPage stuff''
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed "  <> printError  err
  resolveRoute UserProfileRoute = do
    pure UserProfilePage


  resolveRoute GPSFeedRoute = pure $ GPSFeedPage
  resolveRoute PrivacyPolicyRoute = pure $ PrivacyPolicyPage
  resolveRoute TermsOfServiceRoute = pure $ TermsOfServicePage

instance locationFeedAppM :: LocationFeed AppM where
  getFeed coords = do
    baseUrl <- asks (\e -> e.baseUrl)
    eResp <- liftAff $ get json (baseUrl <> "/feed/location?latitude=" <> show (coords.latitude) <> "&longitude=" <> show (coords.longitude))
    case eResp of
      Right (resp) -> 
        case decodeJson resp.body of
          Right (HAPIJson stuff') -> pure $ stuff'
          Left err -> liftEffect $ throwException $ error $ "Json decoding failed" <> show err
      Left err -> liftEffect $ throwException $ error $ "HTTP request failed " <> printError  err 

instance shareUrlAppM :: ShareURL AppM where
  shareUrl url = do
    baseUrl <- asks (\e -> e.uiBaseUrl)
    liftEffect $ NS.shareUrl (baseUrl <> url)

instance userSessionAppM :: UserSession AppM where
  currentUser = do
    inst <- asks (\e -> e.clerkInstance)
    maybeUser <- liftEffect $ Clerk.getUser inst
    case maybeUser of
      Just user -> pure $ Just $ User
        { fullName : Clerk.userUserName user
        , imageUrl : Clerk.userImageUrl user
        , id : Clerk.userUserId user
        }
      Nothing -> pure Nothing

instance renderExternalAppComponentsAppM :: RenderExternalUserComponents AppM where
  renderUserProfile element = do
    inst <- asks (\e -> e.clerkInstance)
    maybeUser <- liftEffect $ Clerk.getUser inst
    case maybeUser of
      Just _ -> liftEffect $ Clerk.mountUserProfile element inst
      Nothing -> pure unit
  openUserProfile = do
    inst <- asks (\e -> e.clerkInstance)
    maybeUser <- liftEffect $ Clerk.getUser inst
    case maybeUser of
      Just _ -> liftEffect $ Clerk.openUserProfile inst
      Nothing -> pure unit

instance sharePostAppM :: SharePost AppM where
  sharePost eventId postContent = do
    inst <- asks (\e -> e.clerkInstance)
    maybeSession <- liftEffect $ Clerk.getSession inst
    case maybeSession of
      Just session -> do
        token <- liftAff $ Clerk.getToken session
        baseUrl <- asks (\e -> e.baseUrl)
        let req' = AX.defaultRequest
              { content = Just $ AXB.json $ encodeJson $ HSAPIJson postContent
              , headers = [RequestHeader "Authorization" ("Bearer " <> token)]
              , method = Left POST
              , url = baseUrl <> "/comment/event/" <> eventId
              }
        eResp <- liftAff $ request req'
        case eResp of
          Right _ -> pure unit
          Left err -> liftEffect $ throwException $ error $ "HTTP request failed " <> printError  err 
      Nothing -> liftEffect $ throwException $ error $ "Login session does not exist"

instance favouriteAppM  :: Favourite AppM where
  favourite eventId = do
    inst <- asks (\e -> e.clerkInstance)
    maybeSession <- liftEffect $ Clerk.getSession inst
    case maybeSession of
      Just session -> do
        token <- liftAff $ Clerk.getToken session
        baseUrl <- asks (\e -> e.baseUrl)
        let req' = AX.defaultRequest
              { content = Just $ AXB.json $ encodeJson $ true
              , headers = [RequestHeader "Authorization" ("Bearer " <> token)]
              , method = Left PUT
              , url = baseUrl <> "/event/" <> eventId <> "/favourite/"
              }
        eResp <- liftAff $ request req'
        case eResp of
          Right _ -> pure unit
          Left err -> liftEffect $ throwException $ error $ "HTTP request failed " <> printError  err 
      Nothing -> liftEffect $ throwException $ error $ "Login session does not exist"

  unfavourite eventId = do
    inst <- asks (\e -> e.clerkInstance)
    maybeSession <- liftEffect $ Clerk.getSession inst
    case maybeSession of
      Just session -> do
        token <- liftAff $ Clerk.getToken session
        baseUrl <- asks (\e -> e.baseUrl)
        let req' = AX.defaultRequest
              { content = Just $ AXB.json $ encodeJson $ false
              , headers = [RequestHeader "Authorization" ("Bearer " <> token)]
              , method = Left PUT
              , url = baseUrl <> "/event/" <> eventId <> "/favourite/"
              }
        eResp <- liftAff $ request req'
        case eResp of
          Right _ -> pure unit
          Left err -> liftEffect $ throwException $ error $ "HTTP request failed " <> printError  err 
      Nothing -> liftEffect $ throwException $ error $ "Login session does not exist"

    



runAppM :: HereaboutEnv -> AppM ~> Aff
runAppM env (AppM m) = runReaderT m env


